// =============================================================
// TIDashboard — Training Institute Analytics Dashboard
// -------------------------------------------------------------
// Enhanced dashboard UI for tenants whose industry is
// `training_institute`. Role-aware:
//   * admin   — org-wide analytics (employees, attendance, dept)
//   * trainer — batch & student analytics
//   * employee— personal attendance heatmap + work activity
//   * student — course progress + learning activity
//
// Non-Training Institute tenants should NOT render this
// component — the parent `<TenantDashboard />` decides based on
// the resolved `industrySlug`.
//
// All data is fetched from
//   /api/dashboard/training-institute/{admin,trainer,employee,student}
// which performs MongoDB aggregations server-side and 5-minute
// caches the result.
// =============================================================

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../services/api';
import AnimatedKpiCard from '../../components/common/AnimatedKpiCard';
import ChartCard from '../../components/charts/ChartCard';
import GlassCard from '../../components/charts/GlassCard';
import EnrollmentSummaryCard from './EnrollmentSummaryCard';
import { BLUE_PALETTE as BLUE } from '../../components/charts/bluePalette';
import {
  LineChart, BarChart, DoughnutChart,
  StackedBarChart, EmptyChart,
} from '../../components/charts/AnalyticsCharts';
import AttendanceHeatmap from '../../components/charts/AttendanceHeatmap';
import './ti-admin.css';

const serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const mediaUrl = (p) => !p ? '' : (String(p).startsWith('http') ? p : `${serverBase}${p}`);
const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const showcaseImage = (item) => item.imageUrl || item.certificateImageUrl || item.thumbnailUrl || '';
const showcaseTitle = (item) => item.title || item.promoTitle || item.studentName || item.reviewerName || 'Showcase';
const showcaseText = (item) => item.subtitle || item.promoText || item.achievement || item.reviewText || item.courseName || item.reviewerCourse || '';
const showcaseVideo = (item) => item.videoUrl || item.promoVideoUrl || '';
const youtubeId = (url = '') => String(url).match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/)?.[1] || '';
const isYoutubeUrl = (url = '') => Boolean(youtubeId(url));
const autoplayVideoUrl = (url = '') => {
  if (!url) return '';
  const id = youtubeId(url);
  if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=1&rel=0&playsinline=1`;
  return mediaUrl(url);
};

// ─── Helper: format an HH:MM-style clock-in/out timestamp ───────
const fmtClock = (d) => d
  ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
  : '—';

const num = (v) => Number.isFinite(Number(v)) ? Number(v) : 0;

// =============================================================
// Admin / Manager / Team Lead
// Glassmorphism + blue-themed analytics for the Training
// Institute admin dashboard.
// =============================================================
export function TIAdminDashboard({ role, refreshKey, themeColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedConversionMonths, setSelectedConversionMonths] = useState([]);
  const [copiedKey, setCopiedKey] = useState('');
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [dashboardResponse, registrationResponse] = await Promise.all([
        api.get('/dashboard/training-institute/admin'),
        api.get('/users/registration-summary').catch(() => null),
      ]);
      const dashboardData = dashboardResponse.data?.data || {};
      const registrationPayload = registrationResponse?.data || {};
      const liveRegistrationSummary = registrationPayload.data
        || (registrationPayload.individual !== undefined ? registrationPayload : null);
      setData({
        ...dashboardData,
        studentRegistrations: liveRegistrationSummary || dashboardData.studentRegistrations || {},
      });
    } catch (e) {
      // Preserve the last confirmed values; a failed request is not zero activity.
      setLoadError(e.response?.data?.message || 'Dashboard data could not be loaded. Please try again.');
    } finally { setLoading(false); }
  }, []);

  // Load on mount + whenever the parent's refreshKey changes.
  // We deliberately do NOT call any parent loader here — that would
  // cause a setState in the parent which re-renders and unmounts us,
  // creating an infinite re-mount / flicker loop.
  useEffect(() => { load(); }, [load, refreshKey]);

  const employees    = data?.employees || {};
  const monthly      = data?.monthlyTrend || [];
  const trend        = data?.trend || {};
  const training     = data?.training || {};
  const pending      = data?.pending || {};
  const revenue      = data?.revenue || {};
  const revenueByMonth = data?.revenueByMonth || [];
  const studentsByCourse = data?.studentsByCourse || [];
  const batchesByCourse  = data?.batchesByCourse  || [];
  // Enquiry analytics — replaces the "Department Attendance
  // Comparison" chart in the admin/team-lead dashboard.
  const enquiries        = data?.enquiries || {};
  const enquiryKpis      = enquiries.kpis || {};
  const enquiryMonthly    = enquiries.monthlyTrend || [];
  const enquiryByCourse   = enquiries.byCourse || [];
  const conversionSource = enquiries.conversionSource || {};
  const enrollmentWindows = data?.enrollmentWindows || {};
  const studentRegistrations = data?.studentRegistrations || {};
  const enquiryTodayCourses = Array.isArray(enquiryKpis.todayCourses) ? enquiryKpis.todayCourses : [];
  const monthlyConversionOptions = useMemo(() => (
    Array.isArray(conversionSource?.months) ? conversionSource.months : []
  ), [conversionSource?.months]);
  const selectedMonthSet = new Set(selectedConversionMonths);
  const conversionSourceLabels = Array.isArray(conversionSource?.sourceMonths) && conversionSource.sourceMonths.length
    ? conversionSource.sourceMonths.map((row) => row.label)
    : monthlyConversionOptions.map((row) => row.label);
  const conversionSourceDatasets = (Array.isArray(conversionSource?.targets) ? conversionSource.targets : [])
    .filter((row) => selectedMonthSet.has(row.key))
    .map((row, index) => {
      const palette = ['#2563eb', '#7c3aed', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#14b8a6', '#8b5cf6'];
      const color = palette[index % palette.length];
      const sourceValueMap = row.values || {};
      return {
        label: `${row.label} enrollments`,
        data: (Array.isArray(conversionSource?.sourceMonths) && conversionSource.sourceMonths.length
          ? conversionSource.sourceMonths
          : monthlyConversionOptions
        ).map((source) => num(sourceValueMap[source.key] || 0)),
        color,
        fill: false,
        pointRadius: 4,
      };
    });
  const conversionChartLabels = conversionSourceLabels.length
    ? conversionSourceLabels
    : monthlyConversionOptions.map((row) => row.label);
  const conversionChartDatasets = conversionSourceDatasets.length
    ? conversionSourceDatasets
    : [{
      label: 'No converted enrollments',
      data: conversionChartLabels.map(() => 0),
      color: 'rgba(148,163,184,0.9)',
      fill: false,
      pointRadius: 3,
    }];
  const hasConversionSourceData = conversionSourceDatasets.some((dataset) => (
    Array.isArray(dataset.data) && dataset.data.some((value) => Number(value || 0) > 0)
  ));
  const studentAttendanceTrend = data?.studentAttendanceTrend || [];
  const staffAttendanceTrend   = data?.staffAttendanceTrend   || [];
  const clockInTrend           = data?.clockInTrend           || [];

  useEffect(() => {
    const availableKeys = monthlyConversionOptions.map((item) => item.key);
    setSelectedConversionMonths((current) => {
      const existing = current.filter((key) => availableKeys.includes(key));
      if (existing.length) return existing;
      return availableKeys.slice(Math.max(0, availableKeys.length - 3));
    });
  }, [monthlyConversionOptions]);

  const toggleConversionMonth = (monthKey) => {
    setSelectedConversionMonths((current) => {
      if (current.includes(monthKey)) {
        if (current.length === 1) return current;
        return current.filter((key) => key !== monthKey);
      }
      return [...current, monthKey];
    });
  };

  const copyCourseSummary = useCallback(async (items, label) => {
    const lines = (Array.isArray(items) ? items : [])
      .map((item) => `${item.count || 0} - ${item.courseName || 'Unspecified Course'}`)
      .join('\n');
    if (!lines) return;
    try {
      await navigator.clipboard.writeText(lines);
      setCopiedKey(label);
      window.setTimeout(() => setCopiedKey((current) => (current === label ? '' : current)), 1600);
    } catch (_) {
      // Clipboard failures are non-fatal for the dashboard.
    }
  }, []);

  if (loadError && !data) return <div className="alert alert-warning" role="alert">{loadError}</div>;

  return (
    <div className="ti-admin-dashboard">
      {loadError && <div className="alert alert-warning" role="alert">{loadError}</div>}
      {/* ── 1. CRM + Enrollment summary cards */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <GlassCard
            title="CRM"
            subtitle="Today, weekly, and monthly enquiry movement"
            icon="bi-megaphone-fill"
            accent={BLUE.primaryDeeper}
            onClick={() => window.location.assign('/crm')}
          >
            <div className="row g-3 align-items-center">
              <div className="col-md-7">
                <div className="row g-2">
                  <div className="col-4">
                    <div className="rounded-3 p-3 h-100" style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.18)' }}>
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Today</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: BLUE.primaryDeeper }}>{num(enquiryKpis.today)}</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="rounded-3 p-3 h-100" style={{ background: 'rgba(14,165,233,0.10)', border: '1px solid rgba(14,165,233,0.18)' }}>
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Weekly</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#0369a1' }}>{num(enquiryKpis.weekly)}</div>
                    </div>
                  </div>
                  <div className="col-4">
                    <div className="rounded-3 p-3 h-100" style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.18)' }}>
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>Monthly</div>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#15803d' }}>{num(enquiryKpis.monthly)}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-5">
                <DoughnutChart
                  height={180}
                  slices={[
                    { label: 'Today', value: num(enquiryKpis.today), color: BLUE.primaryDeeper },
                    { label: 'Weekly', value: num(enquiryKpis.weekly), color: '#0ea5e9' },
                    { label: 'Monthly', value: num(enquiryKpis.monthly), color: '#22c55e' },
                  ].filter((item) => item.value > 0)}
                  centerLabel={num(enquiryKpis.total)}
                  centerSubLabel="Total"
                  themeColor={BLUE.primaryDeeper}
                />
              </div>
            </div>
            {enquiryKpis.todayLeadCategories?.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                {enquiryKpis.todayLeadCategories.map((item, index) => (
                  <span className="badge bg-light text-dark border" key={`${item.category}-${index}`}>
                    {item.label} · {item.courseName}: {item.count}
                  </span>
                ))}
              </div>
            )}
            {enquiryTodayCourses.length > 0 && (
              <div
                className="rounded-3 px-3 py-2 mt-3"
                style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.12)' }}
              >
                <div className="d-flex align-items-center justify-content-between gap-2" style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: BLUE.primaryDeeper, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Today Enquiry Courses
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyCourseSummary(enquiryTodayCourses, 'enquiry');
                    }}
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: BLUE.primaryDeeper,
                      background: 'rgba(255,255,255,0.82)',
                      border: '1px solid rgba(59,130,246,0.16)',
                      padding: '4px 10px',
                      borderRadius: 10,
                    }}
                  >
                    {copiedKey === 'enquiry' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {enquiryTodayCourses.map((item, index) => (
                    <div
                      key={`${item.courseId || item.courseName || 'enquiry'}-${index}`}
                      className="px-2 py-1 rounded-3"
                      style={{ fontSize: 11, color: '#475569', background: 'rgba(255,255,255,0.72)', border: '1px solid rgba(59,130,246,0.12)' }}
                    >
                      <span style={{ fontWeight: 800, color: BLUE.primaryDeeper }}>{item.count || 0}</span> {item.courseName || 'Unspecified Course'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </GlassCard>
        </div>
        <div className="col-lg-6">
          <EnrollmentSummaryCard categories={enrollmentWindows.categories || []} />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          <GlassCard
            title="Student Registrations"
            subtitle="Individual and college registrations in one place"
            icon="bi-mortarboard-fill"
            accent="#6366f1"
          >
            <div className="row g-3 ti-registration-summary">
              <div className="col-md-4">
                <button
                  type="button"
                  className="ti-registration-summary-card ti-registration-summary-card--individual"
                  onClick={() => window.location.assign('/users?tab=students')}
                >
                  <span className="ti-registration-summary-icon"><i className="bi bi-person-fill"></i></span>
                  <span className="ti-registration-summary-copy">
                    <small>Individual Students</small>
                    <strong>{num(studentRegistrations.individual)}</strong>
                    <span>Direct and paid-course registrations</span>
                  </span>
                  <i className="bi bi-arrow-right ti-registration-summary-arrow"></i>
                </button>
              </div>
              <div className="col-md-4">
                <button
                  type="button"
                  className="ti-registration-summary-card ti-registration-summary-card--college"
                  onClick={() => window.location.assign('/users?tab=college-students')}
                >
                  <span className="ti-registration-summary-icon"><i className="bi bi-buildings-fill"></i></span>
                  <span className="ti-registration-summary-copy">
                    <small>College Students</small>
                    <strong>{num(studentRegistrations.college)}</strong>
                    <span>Registered through college portals</span>
                  </span>
                  <i className="bi bi-arrow-right ti-registration-summary-arrow"></i>
                </button>
              </div>
              <div className="col-md-4">
                <button
                  type="button"
                  className="ti-registration-summary-card ti-registration-summary-card--pending"
                  onClick={() => window.location.assign('/lms?tab=college-review')}
                >
                  <span className="ti-registration-summary-icon"><i className="bi bi-hourglass-split"></i></span>
                  <span className="ti-registration-summary-copy">
                    <small>Pending Authorization</small>
                    <strong>{num(studentRegistrations.pendingCollege)}</strong>
                    <span>College students awaiting verification</span>
                  </span>
                  <i className="bi bi-arrow-right ti-registration-summary-arrow"></i>
                </button>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          <GlassCard
            title="Enrollment Conversion Source"
            subtitle="Pick enrollment months to see which enquiry months produced those conversions"
            icon="bi-graph-up-arrow"
            accent={BLUE.primary}
            onClick={() => window.location.assign('/enquiries')}
          >
            <div
              className="rounded-3 px-3 py-2 mb-3"
              style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.12)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: BLUE.primary, marginBottom: 4 }}>
                Enrollment Month Filter
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Example: if you select August 2026, the chart shows whether those enrollments came from June, July, or August enquiries.
              </div>
            </div>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
              <div className="d-flex flex-wrap gap-2">
                {monthlyConversionOptions.map((option) => (
                  <label
                    key={option.key}
                    className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded-3"
                    style={{ border: '1px solid rgba(99,102,241,0.2)', background: 'rgba(99,102,241,0.08)', cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedConversionMonths.includes(option.key)}
                      onChange={() => toggleConversionMonth(option.key)}
                    />
                    <span style={{ fontSize: 12, fontWeight: 700, color: BLUE.primary }}>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="d-flex flex-wrap gap-2 mb-3" onClick={(e) => e.stopPropagation()}>
              <span
                className="px-2 py-1 rounded-3"
                style={{ fontSize: 12, fontWeight: 700, color: '#475569', background: 'rgba(148,163,184,0.12)' }}
              >
                Selected months: {selectedConversionMonths.length}
              </span>
              <span
                className="px-2 py-1 rounded-3"
                style={{ fontSize: 12, fontWeight: 700, color: BLUE.primary, background: 'rgba(59,130,246,0.10)' }}
              >
                X-axis: Enquiry month
              </span>
            </div>
            {!hasConversionSourceData && (
              <div
                className="rounded-3 px-3 py-2 mb-3"
                style={{ background: 'rgba(148,163,184,0.10)', border: '1px solid rgba(148,163,184,0.18)', color: '#64748b', fontSize: 12 }}
              >
                No enquiry-to-enrollment conversion records are available yet for the selected months. The axes are shown so you can still compare periods once conversion data is created.
              </div>
            )}
            {conversionChartLabels.length ? (
              <LineChart
                height={290}
                labels={conversionChartLabels}
                datasets={conversionChartDatasets}
                yMin={0}
                xAxisTitle="Original enquiry month"
                yAxisTitle="Converted enrollments"
              />
            ) : (
              <EmptyChart label="conversion source" />
            )}
          </GlassCard>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-lg-3">
          <AnimatedKpiCard
            icon="bi-journal-bookmark-fill" label="Courses"
            value={training.courseCount} loading={loading}
            color={BLUE.primary} sub="Active courses"
          />
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <AnimatedKpiCard
            icon="bi-collection-fill" label="Batches"
            value={training.batchCount} loading={loading}
            color={BLUE.primaryDark} sub="Running"
          />
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <AnimatedKpiCard
            icon="bi-person-plus-fill" label="Enrollments"
            value={training.enrollmentCount} loading={loading}
            color="#22c55e" sub="Active students"
          />
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <AnimatedKpiCard
            icon="bi-mortarboard-fill" label="Students"
            value={training.studentCount} loading={loading}
            color={BLUE.accent} sub="Total enrolled"
          />
        </div>
        <div className="col-6 col-md-4 col-lg-3">
          <AnimatedKpiCard
            icon="bi-play-circle-fill" label="Free Course Enrollments"
            value={training.freeCourseEnrollmentCount} loading={loading}
            color="#8b5cf6" sub="Open-access learners"
            onClick={() => window.location.assign('/enrollments?source=open_access')}
          />
        </div>
      </div>

      {/* ── 2. Student Attendance vs Staff Attendance (line) ─── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <GlassCard
            title="Students Attendance"
            subtitle="Last 30 days · Daily present %"
            icon="bi-mortarboard" accent={BLUE.primary}
            onClick={() => window.location.assign('/attendance')}
          >
            <LineChart
              height={220}
              labels={studentAttendanceTrend.map(t => t.label)}
              datasets={[{
                label: 'Student Attendance %',
                data: studentAttendanceTrend.map(t => t.pct),
                color: BLUE.primary, fill: true, pointRadius: 2,
              }]}
              themeColor={BLUE.primary}
              yMin={0} yMax={100} yStepSize={25}
            />
          </GlassCard>
        </div>
        <div className="col-lg-6">
          <GlassCard
            title="Staff Attendance"
            subtitle="Last 30 days · Daily present %"
            icon="bi-person-badge" accent={BLUE.primaryDark}
            onClick={() => window.location.assign('/work-attendance')}
          >
            <LineChart
              height={220}
              labels={staffAttendanceTrend.map(t => t.label)}
              datasets={[{
                label: 'Staff Attendance %',
                data: staffAttendanceTrend.map(t => t.pct),
                color: BLUE.primaryDark, fill: true, pointRadius: 2,
              }]}
              themeColor={BLUE.primaryDark}
              yMin={0} yMax={100} yStepSize={25}
            />
          </GlassCard>
        </div>
      </div>

      {/* ── 3. Clock-in analytics (bar — sessions per day) ───── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <GlassCard
            title="Clock-In Analytics"
            subtitle="Last 14 days · Daily login sessions"
            icon="bi-clock-history" accent={BLUE.accent}
            onClick={() => window.location.assign('/work-attendance')}
          >
            <BarChart
              height={220}
              labels={clockInTrend.map(t => t.label)}
              datasets={[{ label: 'Sessions', data: clockInTrend.map(t => t.sessions), color: BLUE.primary }]}
              themeColor={BLUE.primary}
            />
          </GlassCard>
        </div>
        <div className="col-lg-5">
          <GlassCard
            title="No. of Students"
            subtitle="By course (top 8)"
            icon="bi-people" accent={BLUE.primaryDeeper}
            onClick={() => window.location.assign('/enrollments')}
          >
            {studentsByCourse.length ? (
              <BarChart
                height={Math.max(180, studentsByCourse.length * 28)}
                labels={studentsByCourse.map(s => s.courseName.length > 14 ? s.courseName.slice(0, 13) + '…' : s.courseName)}
                datasets={[{ label: 'Students', data: studentsByCourse.map(s => s.count), color: BLUE.primaryDark }]}
                themeColor={BLUE.primaryDark}
                horizontal
                onBarClick={({ index }) => {
                  const c = studentsByCourse[index];
                  if (c) window.location.assign(`/enrollments?course=${encodeURIComponent(c.courseId)}`);
                }}
              />
            ) : <EmptyChart label="students" themeColor={BLUE.primary} />}
          </GlassCard>
        </div>
      </div>

      {/* ── 4. Course comparison (students vs batches) ───────── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <GlassCard
            title="Students per Course"
            subtitle="Top courses by enrollment"
            icon="bi-bar-chart-line" accent={BLUE.primary}
            onClick={() => window.location.assign('/enrollments')}
          >
            {studentsByCourse.length ? (
              <BarChart
                height={Math.max(180, studentsByCourse.length * 28)}
                labels={studentsByCourse.map(s => s.courseName.length > 14 ? s.courseName.slice(0, 13) + '…' : s.courseName)}
                datasets={[{ label: 'Students', data: studentsByCourse.map(s => s.count), color: BLUE.primary }]}
                themeColor={BLUE.primary}
                onBarClick={({ index }) => {
                  const c = studentsByCourse[index];
                  if (c) window.location.assign(`/enrollments?course=${encodeURIComponent(c.courseId)}`);
                }}
              />
            ) : <EmptyChart label="students per course" themeColor={BLUE.primary} />}
          </GlassCard>
        </div>
        <div className="col-lg-6">
          <GlassCard
            title="Batches per Course"
            subtitle="Top courses by batch count"
            icon="bi-collection" accent={BLUE.primaryDark}
            onClick={() => window.location.assign('/batches')}
          >
            {batchesByCourse.length ? (
              <BarChart
                height={Math.max(180, batchesByCourse.length * 28)}
                labels={batchesByCourse.map(s => s.courseName.length > 14 ? s.courseName.slice(0, 13) + '…' : s.courseName)}
                datasets={[{ label: 'Batches', data: batchesByCourse.map(s => s.count), color: BLUE.primaryDark }]}
                themeColor={BLUE.primaryDark}
                onBarClick={({ index }) => {
                  const c = batchesByCourse[index];
                  if (c) window.location.assign(`/batches?course=${encodeURIComponent(c.courseId)}`);
                }}
              />
            ) : <EmptyChart label="batches per course" themeColor={BLUE.primaryDark} />}
          </GlassCard>
        </div>
      </div>

      {/* ── 5. Revenue by month (line) + totals (KPI) ────────── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <GlassCard
            title="Revenue by Month"
            subtitle="Last 12 months · Collected fees"
            icon="bi-currency-rupee" accent={BLUE.primaryDeeper}
            onClick={() => window.location.assign('/enrollments')}
          >
            {revenueByMonth.length ? (
              <LineChart
                height={240}
                labels={revenueByMonth.map(m => m.label)}
                datasets={[{
                  label: 'Revenue (₹)',
                  data: revenueByMonth.map(m => m.revenue),
                  color: BLUE.primary, fill: true, pointRadius: 4,
                }]}
                themeColor={BLUE.primary}
                yMin={0}
              />
            ) : <EmptyChart label="revenue trend" themeColor={BLUE.primary} />}
          </GlassCard>
        </div>
        <div className="col-lg-4">
          <GlassCard
            title="Revenue Snapshot"
            subtitle="Collected & pending"
            icon="bi-wallet2" accent={BLUE.accent}
          >
            <div className="d-flex flex-column gap-3 mt-2">
              <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15) 0%, rgba(29,78,216,0.10) 100%)',
                border: '1px solid rgba(59,130,246,0.25)',
              }}>
                <div>
                  <div style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Total Collected
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: BLUE.primaryDeeper, marginTop: 2 }}>
                    ₹{num(revenue.total).toLocaleString('en-IN')}
                  </div>
                </div>
                <i className="bi bi-check2-circle" style={{ fontSize: 32, color: BLUE.primary, opacity: 0.6 }}></i>
              </div>
              <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{
                background: 'linear-gradient(135deg, rgba(148,163,184,0.18) 0%, rgba(100,116,139,0.10) 100%)',
                border: '1px solid rgba(148,163,184,0.30)',
              }}>
                <div>
                  <div style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Outstanding
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#475569', marginTop: 2 }}>
                    ₹{num(revenue.pending).toLocaleString('en-IN')}
                  </div>
                </div>
                <i className="bi bi-hourglass-split" style={{ fontSize: 32, color: '#94a3b8', opacity: 0.7 }}></i>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>

      {/* ── 6. Monthly attendance (12 months) — full-width ──── */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <GlassCard
            title="Monthly Attendance Trend"
            subtitle="Last 12 months · Present, Absent, Leave, Permission"
            icon="bi-graph-up-arrow" accent={BLUE.primary}
            onClick={() => window.location.assign('/attendance')}
            badge={<span className="badge" style={{
              background: 'rgba(59,130,246,0.15)', color: BLUE.primaryDeeper,
              fontSize: 10, fontWeight: 700, border: '1px solid rgba(59,130,246,0.3)',
            }}>Year view</span>}
          >
            {monthly.length > 0 ? (
              <StackedBarChart
                height={260}
                labels={monthly.map(m => m.label)}
                series={[
                  { label: 'Present',    data: monthly.map(m => m.present),    color: BLUE.primary },
                  { label: 'Absent',     data: monthly.map(m => m.absent),     color: '#94a3b8' },
                  { label: 'Leave',      data: monthly.map(m => m.leave),      color: BLUE.accent },
                  { label: 'Permission', data: monthly.map(m => m.permission), color: BLUE.primaryDark },
                ]}
                themeColor={BLUE.primary}
                onBarClick={({ label }) => window.location.assign(`/attendance?month=${encodeURIComponent(label)}`)}
              />
            ) : (
              <EmptyChart label="attendance trend" themeColor={BLUE.primary} />
            )}
          </GlassCard>
        </div>
      </div>

      {/* ── 7. Enquiries Comparison + Course-based Enquiries ─── */}
      {/* Replaces the old "Department Attendance Comparison"
          chart. Enquiries are the funnel that feeds enrollment, so
          showing them up here gives management a quick read on
          demand-side health. */}
      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <GlassCard
            title="Enquiries Comparison"
            subtitle="Last 12 months · New enquiries vs. month"
            icon="bi-megaphone-fill" accent={BLUE.primary}
            onClick={() => window.location.assign('/enquiries')}
          >
            {enquiryMonthly.length ? (
              <LineChart
                height={220}
                labels={enquiryMonthly.map(m => m.label)}
                datasets={[{
                  label: 'New Enquiries',
                  data: enquiryMonthly.map(m => m.count),
                  color: BLUE.primary, fill: true, pointRadius: 3,
                }]}
                themeColor={BLUE.primary}
                yMin={0}
                onPointClick={({ label }) =>
                  window.location.assign(`/enquiries?month=${encodeURIComponent(label)}`)
                }
              />
            ) : <EmptyChart label="enquiries" themeColor={BLUE.primary} />}
          </GlassCard>
        </div>
        <div className="col-lg-6">
          <GlassCard
            title="Course-Based Enquiries"
            subtitle="Top courses by enquiry count"
            icon="bi-bookmark-star-fill" accent={BLUE.primaryDark}
            onClick={() => window.location.assign('/enquiries')}
          >
            {enquiryByCourse.length ? (
              <BarChart
                height={Math.max(180, enquiryByCourse.length * 32)}
                labels={enquiryByCourse.map(c => c.courseName.length > 18 ? c.courseName.slice(0, 17) + '…' : c.courseName)}
                datasets={[{
                  label: 'Enquiries',
                  data: enquiryByCourse.map(c => c.count),
                  color: BLUE.primaryDark,
                }]}
                themeColor={BLUE.primaryDark}
                onBarClick={({ index }) => {
                  const c = enquiryByCourse[index];
                  if (c) window.location.assign(`/enquiries?course=${encodeURIComponent(c.courseId)}`);
                }}
              />
            ) : <EmptyChart label="course enquiries" themeColor={BLUE.primaryDark} />}
          </GlassCard>
        </div>
      </div>

      {/* ── 8. Pending items (attendance + leaves + tasks) ─── */}
      {/* The admin/team-lead dashboard now puts the operational
          follow-ups (today's attendance + pending leaves + tasks)
          together at the bottom of the page, so the top half
          focuses on the funnel / pipeline metrics. */}
      <div className="row g-3 mb-4">
        <div className="col-lg-4">
          <GlassCard
            title="Today's Attendance"
            subtitle="Distribution of statuses"
            icon="bi-pie-chart-fill" accent={BLUE.primary}
            onClick={() => window.location.assign('/attendance')}
          >
            {(() => {
              const slices = [
                { label: 'Present', value: employees.presentToday || 0, color: BLUE.primary },
                { label: 'Absent',  value: employees.absentToday  || 0, color: '#94a3b8' },
                { label: 'Leave',   value: employees.onLeaveToday || 0, color: BLUE.accent },
                { label: 'Late',    value: employees.lateLoginCount || 0, color: BLUE.primaryDark },
              ].filter(s => s.value > 0);
              const total = slices.reduce((s, x) => s + x.value, 0) || 1;
              const presentPct = Math.round(((employees.presentToday || 0) / total) * 100);
              return slices.length ? (
                <DoughnutChart
                  height={220}
                  slices={slices}
                  centerLabel={`${presentPct}%`}
                  centerSubLabel="Present"
                  themeColor={BLUE.primary}
                  onSliceClick={() => window.location.assign('/attendance')}
                />
              ) : <EmptyChart label="today" themeColor={BLUE.primary} />;
            })()}
          </GlassCard>
        </div>
        <div className="col-md-4">
          <GlassCard
            title="Pending Leaves"
            subtitle="Awaiting your review"
            icon="bi-calendar-x-fill" accent={BLUE.accent}
            onClick={() => window.location.assign('/hrm/leave')}
          >
            <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{
              background: 'linear-gradient(135deg, rgba(59,130,246,0.12) 0%, rgba(96,165,250,0.08) 100%)',
              border: '1px solid rgba(59,130,246,0.25)',
            }}>
              <div>
                <div style={{ fontSize: 36, fontWeight: 800, color: BLUE.primaryDeeper, lineHeight: 1 }}>
                  {pending.leaves || 0}
                </div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 4, fontWeight: 500 }}>
                  decisions pending
                </div>
              </div>
              <i className="bi bi-inbox-fill" style={{ fontSize: 48, color: BLUE.primary, opacity: 0.5 }}></i>
            </div>
          </GlassCard>
        </div>
        <div className="col-md-4">
          <GlassCard
            title="Pending Tasks"
            subtitle={`${pending.totalTasks || 0} total in queue`}
            icon="bi-list-task" accent={BLUE.primaryDeeper}
            onClick={() => window.location.assign('/tasks')}
          >
            <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{
              background: 'linear-gradient(135deg, rgba(30,58,138,0.10) 0%, rgba(59,130,246,0.06) 100%)',
              border: '1px solid rgba(30,58,138,0.20)',
            }}>
              <div>
                <div style={{ fontSize: 36, fontWeight: 800, color: BLUE.primaryDeeper, lineHeight: 1 }}>
                  {pending.tasks || 0}
                </div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 4, fontWeight: 500 }}>
                  not yet completed
                </div>
              </div>
              <i className="bi bi-check2-square" style={{ fontSize: 48, color: BLUE.primaryDeeper, opacity: 0.5 }}></i>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Trainer
// =============================================================
export function TITrainerDashboard({ refreshKey, themeColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/training-institute/trainer');
      setData(r.data?.data || {});
    } catch { setData({}); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const batches = data?.batches || {};
  const students = data?.students || {};
  const classes = data?.classes || {};
  const trend = data?.attendanceTrend || [];
  const batchWise = data?.batchWise || [];
  const completion = data?.courseCompletion || { completed: 0, inProgress: 0, pending: 0 };
  const assessments = data?.assessments || {};

  const completionSlices = [
    { label: 'Completed',   value: completion.completed,  color: '#22c55e' },
    { label: 'In Progress', value: completion.inProgress, color: '#6366f1' },
    { label: 'Pending',     value: completion.pending,    color: '#f59e0b' },
  ].filter(s => s.value > 0);

  return (
    <div>
      {/* ── Batch Performance KPIs ─────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-people-fill" label="Total Students"
            value={students.total} loading={loading}
            color="#0ea5e9" sub="Across my batches"
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-collection-fill" label="Active Batches"
            value={batches.active} loading={loading}
            color="#6366f1" sub={`${batches.total || 0} total`}
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-mortarboard-fill" label="Completed Classes"
            value={classes.completed} loading={loading}
            color="#22c55e" sub={`${classes.total || 0} total sessions`}
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-calendar-event-fill" label="Upcoming Classes"
            value={classes.upcoming} loading={loading}
            color="#f59e0b" sub="Sessions remaining"
          />
        </div>
      </div>

      {/* ── Attendance Trend + Course Completion ──────────── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <ChartCard
            title="Student Attendance Trend"
            subtitle="Last 14 days · Daily attendance percentage"
            icon="bi-graph-up" iconColor="#6366f1"
            onClick={() => window.location.assign('/attendance')}
          >
            {trend.length ? (
              <LineChart
                height={260}
                labels={trend.map(t => t.label)}
                datasets={[{
                  label: 'Attendance %', data: trend.map(t => t.pct),
                  color: themeColor, fill: true, pointRadius: 3,
                }]}
                themeColor={themeColor}
                yMin={0} yMax={100} yStepSize={25}
                onPointClick={({ label }) => window.location.assign(`/attendance?date=${encodeURIComponent(label)}`)}
              />
            ) : <EmptyChart label="attendance trend" themeColor={themeColor} />}
          </ChartCard>
        </div>

        <div className="col-lg-5">
          <ChartCard
            title="Course Completion"
            subtitle="Across my assigned courses"
            icon="bi-pie-chart-fill" iconColor="#22c55e"
            onClick={() => window.location.assign('/course-records')}
          >
            {completionSlices.length ? (
              <DoughnutChart
                height={260}
                slices={completionSlices}
                centerLabel={completion.completed + completion.inProgress + completion.pending}
                centerSubLabel="Courses"
                themeColor={themeColor}
              />
            ) : <EmptyChart label="courses" themeColor={themeColor} />}
          </ChartCard>
        </div>
      </div>

      {/* ── Batch-wise Attendance ──────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <ChartCard
            title="Batch-Wise Attendance"
            subtitle="Last 30 days · Average % per batch"
            icon="bi-bar-chart-fill" iconColor="#0ea5e9"
            onClick={() => window.location.assign('/attendance')}
          >
            {batchWise.length ? (
              <BarChart
                height={Math.max(180, batchWise.length * 36)}
                labels={batchWise.map(b => b.name)}
                datasets={[{ label: 'Attendance %', data: batchWise.map(b => b.pct), color: themeColor }]}
                themeColor={themeColor}
                yMax={100}
                onBarClick={({ index }) => {
                  const b = batchWise[index];
                  if (b) window.location.assign(`/attendance?batch=${encodeURIComponent(b.batchId)}`);
                }}
              />
            ) : <EmptyChart label="batches" themeColor={themeColor} />}
          </ChartCard>
        </div>

        <div className="col-lg-5">
          <ChartCard
            title="Assessments"
            subtitle="Active and pending evaluation"
            icon="bi-clipboard-check" iconColor="#8b5cf6"
            onClick={() => window.location.assign('/assessments')}
          >
            <div className="d-flex flex-column gap-2 mt-2">
              <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{ background: '#f3e8ff' }}>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#8b5cf6' }}>{assessments.active || 0}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Active Assessments</div>
                </div>
                <i className="bi bi-clipboard-check" style={{ fontSize: 32, color: '#8b5cf6', opacity: 0.4 }}></i>
              </div>
              <div className="d-flex align-items-center justify-content-between p-3 rounded" style={{
                background: (assessments.pendingEvaluations || 0) > 0 ? '#fee2e2' : '#d1fae5',
              }}>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: (assessments.pendingEvaluations || 0) > 0 ? '#ef4444' : '#22c55e' }}>
                    {assessments.pendingEvaluations || 0}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Pending Evaluation</div>
                </div>
                <i className="bi bi-hourglass-split" style={{ fontSize: 32, color: (assessments.pendingEvaluations || 0) > 0 ? '#ef4444' : '#22c55e', opacity: 0.4 }}></i>
              </div>
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Employee
// =============================================================
export function TIEmployeeDashboard({ refreshKey, themeColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/training-institute/employee');
      setData(r.data?.data || {});
    } catch { setData({}); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const wd = data?.workingDays || {};
  const heatmap = data?.heatmap || [];
  const workActivity = data?.workActivity || [];
  const trend = data?.attendanceTrend || [];
  const attPct = data?.attendancePct;
  const permHours = data?.permissionHours || 0;

  return (
    <div>
      {/* ── Personal KPIs ─────────────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-calendar3" label="Working Days"
            value={wd.total} loading={loading}
            color="#6366f1" sub="Last 90 days"
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-check2-circle" label="Present Days"
            value={wd.present} loading={loading}
            color="#22c55e"
            sub={attPct != null ? `${attPct}% attendance` : ''}
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-calendar-x" label="Leave Taken"
            value={wd.leave + wd.absent} loading={loading}
            color="#f59e0b" sub={`${wd.absent || 0} absent · ${wd.leave || 0} leave`}
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-clock-history" label="Permission Hours"
            value={permHours} loading={loading}
            color="#8b5cf6" suffix=" hrs" sub="This year"
          />
        </div>
      </div>

      {/* ── Attendance Heatmap + Trend line ────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <ChartCard
            title="My Attendance History"
            subtitle="Last 90 days · Calendar heatmap"
            icon="bi-grid-3x3-gap-fill" iconColor="#22c55e"
            onClick={() => window.location.assign('/work-attendance')}
          >
            <AttendanceHeatmap
              days={heatmap}
              onCellClick={() => window.location.assign('/work-attendance')}
            />
          </ChartCard>
        </div>
        <div className="col-lg-5">
          <ChartCard
            title="Attendance Trend"
            subtitle="Last 30 days · % score per day"
            icon="bi-graph-up" iconColor="#6366f1"
            onClick={() => window.location.assign('/work-attendance')}
          >
            {trend.length ? (
              <LineChart
                height={280}
                labels={trend.map(t => t.label)}
                datasets={[{
                  label: 'Attendance %', data: trend.map(t => t.value ?? null),
                  color: themeColor, fill: true, pointRadius: 2,
                }]}
                themeColor={themeColor}
                yMin={0} yMax={100} yStepSize={25}
              />
            ) : <EmptyChart label="trend" themeColor={themeColor} />}
          </ChartCard>
        </div>
      </div>

      {/* ── Work Activity Timeline ────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <ChartCard
            title="Work Activity Timeline"
            subtitle="Last 7 days · Clock In / Last Activity / Clock Out"
            icon="bi-clock-fill" iconColor="#0ea5e9"
            onClick={() => window.location.assign('/work-attendance')}
          >
            <div className="d-flex flex-column gap-2">
              {workActivity.map(d => (
                <div key={d.key}
                  className="d-flex align-items-center gap-2 p-2 rounded"
                  style={{ background: '#f8fafc' }}
                >
                  <div style={{ width: 50, fontSize: 12, fontWeight: 600, color: '#475569' }}>
                    {d.label}
                  </div>
                  <div className="d-flex align-items-center flex-grow-1 gap-2" style={{ position: 'relative' }}>
                    {/* clock-in dot */}
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: '#22c55e', flexShrink: 0 }} title="Clock In"/>
                    <div style={{ fontSize: 12, color: '#1e293b', minWidth: 70 }}>
                      <i className="bi bi-box-arrow-in-right me-1 text-success"></i>{fmtClock(d.clockIn)}
                    </div>
                    {/* line */}
                    <div style={{ flexGrow: 1, height: 2, background: 'linear-gradient(90deg, #22c55e, #0ea5e9, #ef4444)', borderRadius: 1 }}/>
                    {/* last activity */}
                    <div style={{ fontSize: 12, color: '#0ea5e9', minWidth: 70, textAlign: 'center' }}>
                      <i className="bi bi-activity me-1"></i>{fmtClock(d.lastActivity)}
                    </div>
                    <div style={{ flexGrow: 1, height: 2, background: 'linear-gradient(90deg, #0ea5e9, #ef4444)', borderRadius: 1 }}/>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: '#ef4444', flexShrink: 0 }} title="Clock Out"/>
                    <div style={{ fontSize: 12, color: '#1e293b', minWidth: 70, textAlign: 'right' }}>
                      <i className="bi bi-box-arrow-right me-1 text-danger"></i>{fmtClock(d.clockOut)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Student
// =============================================================
function StudentShowcaseSection({ items = [] }) {
  const imageItems = items.filter(item => item.type !== 'video_review' && showcaseImage(item));
  const videoItems = items.filter(item => showcaseVideo(item));
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (imageItems.length <= 1) return undefined;
    const timer = setInterval(() => {
      setActive(idx => (idx + 1) % imageItems.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [imageItems.length]);

  useEffect(() => {
    if (imageItems.length > 0 && active >= imageItems.length) setActive(0);
  }, [active, imageItems.length]);

  if (!items.length) return null;

  const current = imageItems[active] || imageItems[0];
  const move = (dir) => {
    if (!imageItems.length) return;
    setActive(idx => (idx + dir + imageItems.length) % imageItems.length);
  };

  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
            <i className="bi bi-stars me-2 text-warning"></i>Student Showcase
          </div>
          {imageItems.length > 1 && (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-light btn-sm rounded-circle" onClick={() => move(-1)} aria-label="Previous showcase">
                <i className="bi bi-chevron-left"></i>
              </button>
              <button type="button" className="btn btn-light btn-sm rounded-circle" onClick={() => move(1)} aria-label="Next showcase">
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </div>

        <div className="row g-3 align-items-stretch">
          {current && (
            <div className={videoItems.length ? 'col-lg-7' : 'col-12'}>
              <div className="h-100 overflow-hidden" style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#f8fafc' }}>
                <div style={{ position: 'relative', minHeight: 240, background: current.bgColor || '#0f172a' }}>
                  <img
                    src={mediaUrl(showcaseImage(current))}
                    alt={showcaseTitle(current)}
                    style={{ width: '100%', height: '100%', minHeight: 240, maxHeight: 360, objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 16, background: 'linear-gradient(180deg, rgba(15,23,42,0), rgba(15,23,42,.82))', color: '#fff' }}>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>{showcaseTitle(current)}</div>
                    {showcaseText(current) && <div style={{ fontSize: 13, opacity: .9, marginTop: 3 }}>{showcaseText(current)}</div>}
                    {current.batchName && <div style={{ fontSize: 12, opacity: .82, marginTop: 4 }}>Batch {current.batchName}</div>}
                  </div>
                </div>
                {imageItems.length > 1 && (
                  <div className="d-flex justify-content-center gap-2 p-3">
                    {imageItems.map((item, idx) => (
                      <button
                        type="button"
                        key={item._id || idx}
                        onClick={() => setActive(idx)}
                        aria-label={`Show slide ${idx + 1}`}
                        style={{
                          width: idx === active ? 24 : 8,
                          height: 8,
                          borderRadius: 8,
                          border: 0,
                          padding: 0,
                          background: idx === active ? '#6366f1' : '#cbd5e1',
                          transition: 'all .2s ease',
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {videoItems.length > 0 && (
            <div className={current ? 'col-lg-5' : 'col-12'}>
              <div className="d-flex gap-3 h-100" style={{ overflowX: 'auto', paddingBottom: 4 }}>
                {videoItems.slice(0, 3).map((item, idx) => {
                  const rawVideo = showcaseVideo(item);
                  const src = autoplayVideoUrl(rawVideo);
                  return (
                    <div key={item._id || idx} style={{ minWidth: current ? 260 : 300, flex: current ? '1 0 260px' : '0 0 300px', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                      <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#020617' }}>
                        {src && isYoutubeUrl(rawVideo) ? (
                          <iframe
                            src={src}
                            title={showcaseTitle(item)}
                            allow="autoplay; encrypted-media; picture-in-picture"
                            allowFullScreen
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                          />
                        ) : src ? (
                          <video
                            src={src}
                            autoPlay
                            muted
                            loop
                            playsInline
                            controls
                            poster={showcaseImage(item) ? mediaUrl(showcaseImage(item)) : undefined}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center text-white" style={{ position: 'absolute', inset: 0 }}>
                            <i className="bi bi-camera-video" style={{ fontSize: 34 }}></i>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{showcaseTitle(item)}</div>
                        {showcaseText(item) && <div className="text-muted mt-1" style={{ fontSize: 12 }}>{showcaseText(item)}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function TIStudentDashboard({ refreshKey, themeColor }) {
  const [data, setData] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, promoRes] = await Promise.all([
        api.get('/dashboard/training-institute/student'),
        api.get('/training/promotions').catch(() => ({ data: { data: [] } })),
      ]);
      setData(r.data?.data || {});
      const promoData = promoRes.data?.data;
      setPromotions(Array.isArray(promoData) ? promoData : (promoData?.records || promoRes.data?.records || []));
    } catch { setData({}); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const enr = data?.enrollments || {};
  const slices = data?.courseProgressSlices || [];
  const activity = data?.learningActivity || [];
  const att = data?.attendance || { pct: null, present: 0, total: 0 };
  const fees = data?.fees || { totalFees: 0, paid: 0, balance: 0, nextDue: null };
  const avgProgress = data?.avgProgress ?? 0;
  const showcase = promotions.filter(p => ['carousel_slide', 'promotion', 'certificate_showcase', 'video_review'].includes(p.type)).slice(0, 6);

  return (
    <div>
      {/* ── Personal KPIs ─────────────────────────────────── */}
      <StudentShowcaseSection items={showcase} />

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-journal-bookmark-fill" label="Enrolled Courses"
            value={enr.total} loading={loading}
            color="#6366f1"
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-patch-check-fill" label="Completed"
            value={enr.completed} loading={loading}
            color="#22c55e"
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-hourglass-split" label="Pending Lessons"
            value={enr.pending} loading={loading}
            color="#f59e0b"
          />
        </div>
        <div className="col-6 col-md-3">
          <AnimatedKpiCard
            icon="bi-bar-chart-line-fill" label="Average Progress"
            value={avgProgress} loading={loading}
            color="#8b5cf6" suffix="%"
            sub={att.pct != null ? `Attendance: ${att.pct}%` : ''}
          />
        </div>
      </div>

      {/* ── Course Progress Donut + Learning Activity ─────── */}
      <div className="row g-3 mb-4">
        <div className="col-12">
          <div className="row g-3 mb-1">
            <div className="col-6 col-md-4">
              <AnimatedKpiCard icon="bi-receipt" label="Full Fees" value={money(fees.totalFees)} loading={loading} color="#0f766e" />
            </div>
            <div className="col-6 col-md-4">
              <AnimatedKpiCard icon="bi-wallet2" label="Paid" value={money(fees.paid)} loading={loading} color="#16a34a" />
            </div>
            <div className="col-12 col-md-4">
              <AnimatedKpiCard
                icon="bi-cash-coin" label="Balance"
                value={money(fees.balance)} loading={loading}
                color={fees.balance > 0 ? '#dc2626' : '#16a34a'}
                sub={fees.nextDue?.amount ? `Next due ${money(fees.nextDue.amount)}` : ''}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <ChartCard
            title="Course Progress"
            subtitle="Completed / In Progress / Not Started"
            icon="bi-pie-chart-fill" iconColor="#6366f1"
            onClick={() => window.location.assign('/student-course')}
          >
            {slices.length ? (
              <DoughnutChart
                height={260}
                slices={slices}
                centerLabel={`${avgProgress}%`}
                centerSubLabel="Avg Progress"
                themeColor={themeColor}
              />
            ) : <EmptyChart label="courses" themeColor={themeColor} />}
          </ChartCard>
        </div>
        <div className="col-lg-7">
          <ChartCard
            title="Learning Activity"
            subtitle="Last 14 days · Daily attendance %"
            icon="bi-graph-up" iconColor="#22c55e"
            onClick={() => window.location.assign('/student-course')}
          >
            {activity.length ? (
              <LineChart
                height={260}
                labels={activity.map(a => a.label)}
                datasets={[{
                  label: 'Activity %', data: activity.map(a => a.pct),
                  color: themeColor, fill: true, pointRadius: 3,
                }]}
                themeColor={themeColor}
                yMin={0} yMax={100} yStepSize={25}
              />
            ) : <EmptyChart label="learning activity" themeColor={themeColor} />}
          </ChartCard>
        </div>
      </div>

      {/* ── My Courses list ──────────────────────────────── */}
      {(data?.myCourses || []).length > 0 && (
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
          <div className="card-body">
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 12 }}>
              <i className="bi bi-collection-play me-2 text-primary"></i>My Courses
            </div>
            <div className="row g-2">
              {data.myCourses.map(c => {
                // Students don't have access to the management-side
                // /enrollments page — route them to the student-friendly
                // course view scoped to this enrollment. Falls back to
                // /student-course when the courseId isn't known.
                const courseHref = c.courseId
                  ? `/student-course?course=${encodeURIComponent(c.courseId)}${c._id ? `&enrollment=${encodeURIComponent(c._id)}` : ''}`
                  : '/student-course';
                return (
                  <div key={c._id} className="col-md-6">
                    <a href={courseHref} className="d-block p-3 rounded h-100 text-decoration-none"
                       style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: 'inherit', transition: 'background .15s ease' }}
                       onMouseEnter={e2 => e2.currentTarget.style.background = '#eef2ff'}
                       onMouseLeave={e2 => e2.currentTarget.style.background = '#f8fafc'}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{c.courseName || 'Course'}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>Batch {c.batchNumber || '—'}</div>
                      <div className="progress mt-2" style={{ height: 6 }}>
                        <div className="progress-bar bg-primary"
                          style={{ width: `${c.progress || 0}%` }}
                        />
                      </div>
                      <div className="d-flex justify-content-between mt-1 align-items-center">
                        <span style={{ fontSize: 11, color: '#64748b' }}>{c.progress || 0}% complete</span>
                        <span className="d-flex gap-1 align-items-center">
                          <span className="badge" style={{
                            background: c.status === 'completed' ? '#d1fae5' : '#ede9fe',
                            color:      c.status === 'completed' ? '#166534' : '#6366f1',
                            fontSize: 10,
                          }}>{c.status}</span>
                          <i className="bi bi-chevron-right" style={{ color: '#94a3b8', fontSize: 12 }}></i>
                        </span>
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
